/**
 * Solid bindings for the gesture recognizer.
 *
 * `pointerHandlers()` returns JSX handlers using Pointer Events + pointer
 * capture (modern; preferred for per-element gestures like swipe-to-action on a
 * ticket line or a KDS line). Bind on an element with an appropriate
 * `touch-action` (e.g. `touch-pan-y` for a horizontal swipe) so the browser
 * yields the right axis to native scroll.
 *
 * Only the per-element pointer adapter is ported here: Malla has no global
 * page-swipe / window-bound gesture, so the Touch+Mouse window adapters from the
 * canon are intentionally omitted (no consumers).
 */

import type { GestureRecognizer } from "./types";

interface PointerHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

/**
 * JSX pointer handlers for a single element. Captures the pointer once the
 * gesture engages (so it keeps tracking outside the element) and releases on
 * up/cancel.
 */
export function pointerHandlers(
  recognizer: GestureRecognizer,
  options: { capture?: boolean } = {},
): PointerHandlers {
  const capture = options.capture ?? true;
  let capturedId: number | null = null;

  return {
    onPointerDown(e) {
      if (e.pointerType === "mouse" && e.button !== 0) {
        return;
      }
      recognizer.start(e.clientX, e.clientY, { target: e.target });
    },
    onPointerMove(e) {
      const wasEngaged = recognizer.isEngaged();
      recognizer.move(e.clientX, e.clientY, () => {
        e.preventDefault();
      });
      if (capture && !wasEngaged && recognizer.isEngaged() && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
          capturedId = e.pointerId;
        } catch {
          /* capture is best-effort */
        }
      }
    },
    onPointerUp(e) {
      recognizer.end(e.clientX, e.clientY);
      if (capturedId !== null && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.releasePointerCapture(capturedId);
        } catch {
          /* element may already be detached */
        }
        capturedId = null;
      }
    },
    onPointerCancel() {
      recognizer.cancel();
      capturedId = null;
    },
  };
}
